// store/Reducer/aiApi.js
//
// Provider-agnostic AI layer for the masjidHub builder. This is the front-end
// counterpart to the documentation's lib/ai/index.ts `chatJSON`.
//
// IMPORTANT: the AI provider (DeepSeek / Gemini / OpenAI) and the secret API key
// live ENTIRELY on the existing backend (https://masjidhub.org). This client
// never sees a key — it just calls two server endpoints. Provider switching,
// rate limiting, subscription checks, quota/usage tracking and prompt building
// are all the backend's responsibility, exactly as the doc requires
// ("server-only API key", "every AI call lives in a 'use server' file").
//
// The backend contract these endpoints expect is documented in
// docs/AI_BACKEND_CONTRACT.md.
//
// Reuses createCustomFetchBaseQuery so the user's auth token and the shared
// 401-handling are applied automatically, identical to every other API slice.

import { createApi } from "@reduxjs/toolkit/query/react";
import { createCustomFetchBaseQuery } from "../baseQuery";

export const aiApi = createApi({
  reducerPath: "aiApi",
  baseQuery: createCustomFetchBaseQuery(),
  endpoints: (builder) => ({
    // Full-store generation: AI fills an entire template's settings from a
    // brand prompt. Returns a DRAFT payload only — nothing is persisted server
    // side here; the client normalizes and applies it into the undoable Redux
    // draft, and the existing "Save Template settings" flow publishes it.
    generateTemplate: builder.mutation({
      query: (body) => ({
        url: "/api/ai/generate-template",
        method: "POST",
        body,
      }),
    }),

    // Single-section rewrite: AI returns a complete settings object for ONE
    // section given a free-text or quick-action instruction.
    rewriteSection: builder.mutation({
      query: (body) => ({
        url: "/api/ai/rewrite-section",
        method: "POST",
        body,
      }),
    }),

    // Prompt-built websites (template id 4). The backend checks every piece of
    // HTML it returns, and checks the whole site again on every save.
    sitePlan: builder.mutation({
      query: (body) => ({ url: "/api/ai/site/plan", method: "POST", body }),
    }),
    siteSection: builder.mutation({
      query: (body) => ({ url: "/api/ai/site/section", method: "POST", body }),
    }),
    siteSectionEdit: builder.mutation({
      query: (body) => ({ url: "/api/ai/site/section/edit", method: "POST", body }),
    }),
    sitePhotos: builder.query({
      query: (q) => ({ url: `/api/ai/site/photos?q=${encodeURIComponent(q)}`, method: "GET" }),
    }),
  }),
});

export const {
  useGenerateTemplateMutation,
  useRewriteSectionMutation,
  useSitePlanMutation,
  useSiteSectionMutation,
  useSiteSectionEditMutation,
  useLazySitePhotosQuery,
} = aiApi;
